import { useEffect, useRef, useState } from "react";
import { Progress } from "./ui/progress";
import { Loader2 } from "lucide-react";

/**
 * Realistic-looking progress bar for long-running AI operations.
 * Since we cannot get real progress from the model, we simulate a curve that:
 *   • advances quickly through the first phases
 *   • slows down in the long "vision analysis" phase (asymptotically approaches `softCap`)
 *   • snaps to 100% the moment `active` flips back to false (operation complete)
 *
 * Props:
 *   active   – boolean, true while the AI call is in flight
 *   phases   – array of { until: number 0..100, label: string }, sorted by `until`
 *   softCap  – upper bound while still active (default 92)
 *   onDone   – optional callback fired once at 100%
 */
const DEFAULT_PHASES = [
  { until: 12, label: "Preparazione immagine..." },
  { until: 30, label: "Invio al modello AI..." },
  { until: 85, label: "Analisi vision in corso..." },
  { until: 100, label: "Elaborazione risultati..." },
];

export default function AiProgressBar({ active, phases = DEFAULT_PHASES, softCap = 92, onDone, className = "", testid = "ai-progress" }) {
  const [pct, setPct] = useState(0);
  const startRef = useRef(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (active) {
      // Starting / continuing
      startRef.current = Date.now();
      finishedRef.current = false;
      setPct(0);

      const id = setInterval(() => {
        const elapsed = (Date.now() - startRef.current) / 1000; // seconds
        // Asymptotic curve: pct = softCap * (1 - e^(-elapsed/tau))
        // tau≈8 makes it reach ~80% around 14s, ~92% around 25s.
        const tau = 8;
        const target = softCap * (1 - Math.exp(-elapsed / tau));
        setPct((prev) => Math.max(prev, Math.min(softCap, target)));
      }, 200);
      return () => clearInterval(id);
    }
    // Operation finished → animate to 100 then hold there briefly
    if (!finishedRef.current && startRef.current) {
      finishedRef.current = true;
      let p = pct;
      const id = setInterval(() => {
        p = Math.min(100, p + 4);
        setPct(p);
        if (p >= 100) {
          clearInterval(id);
          if (typeof onDone === "function") onDone();
        }
      }, 30);
      return () => clearInterval(id);
    }
    // Idle / never started
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active && !startRef.current) return null;

  const phase = phases.find((p) => pct <= p.until) || phases[phases.length - 1];

  return (
    <div className={`space-y-2 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-gray-700 font-medium">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600" />
          {phase.label}
        </span>
        <span className="font-mono text-indigo-700" data-testid={`${testid}-pct`}>{Math.round(pct)}%</span>
      </div>
      <Progress value={pct} className="h-2" />
    </div>
  );
}
